import {
    Box,
    Center,
    Heading,
    useToast,
    Spinner,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import ProfileContext, {
    ProfileError,
    getProfileErrorText,
    getProfileErrorHeader,
} from '../context/ProfileContext';
import { LicenseCreationParams, LicenseForm } from '../forms/LicenseForm';
import { Timeout } from '../util/types';

export const CreateGame: React.FC<{}> = () => {
    const { keycloak } = useKeycloak();
    const profileContext = useContext(ProfileContext);
    const navigate = useNavigate();
    const toast = useToast();
    const controller = new AbortController();
    const signal = controller.signal;
    const timeoutRef = useRef<Timeout>();
    const timeoutRef2 = useRef<Timeout>();
    const [isGameCreated, setIsGameCreated] = useState(false);
    const [submittingGame, setSubmittingGame] = useState(false);
    const [errors, setErrors] = useState<ProfileError[]>([]);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const licenseChecker = () => {
        if (!isGameCreated) {
            console.log('Experiencing a slow response time...');
            return toast({
                title: 'Still Trying',
                description: 'Hmm, this is taking longer than expected...',
                status: 'warning',
                duration: 9000,
                variant: 'subtle',
                isClosable: true,
            });
        }
    };

    const cancelCall = () => {
        if (!isGameCreated) {
            console.log('Request aborted.');
            controller.abort();
            onOpen();
            setErrors((errors) => errors.concat(ProfileError.REQUEST_ABORTED));
            return toast({
                title: 'Request Timed Out',
                description: "We're sorry! please try again later.",
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        if (submittingGame) {
            timeoutRef.current = setTimeout(licenseChecker, 9000);
            timeoutRef2.current = setTimeout(cancelCall, 30000);
            return () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                if (timeoutRef2.current) clearTimeout(timeoutRef2.current);
            };
        }
    }, [submittingGame]);

    useEffect(() => {
        setErrors([]);
    }, []);
    const [makeOwner, setMakeOwner] = useState(false);
    const [adminCheckbox, setAdminCheckbox] = useState(false);

    const createGameLicense = async (params: LicenseCreationParams) => {
        setSubmittingGame(true);
        try {
            const resp = await fetch(`${config.licenseServiceURL}/license`, {
                method: 'POST',
                signal: signal,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${keycloak.token}`,
                },
                body: JSON.stringify({
                    expiresAt: '2030-12-28T00:00:00.000Z',
                    configs: {
                        name: params.name,
                    },
                    becomeOwner: makeOwner,
                    distributionInfo: params.distInfo,
                }),
            });
            if (resp.status !== 200) {
                return toast({
                    title: 'There was an error',
                    description: 'Please try creating the game again.',
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                });
            }
            toast({
                title: 'Success!',
                description: 'Game has been created. Cheers!',
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
            const json = await resp.json();
            profileContext.licenses[json.data.key] = json.data;
            if (json) {
                setIsGameCreated(true);
            }

            // Force refresh token to get a new token containing the new license
            await keycloak.updateToken(-1);
            navigate(`/game/${json.data.key}/overview`);
        } catch (error) {
            if (signal.aborted) {
                throw error;
            }
        }
    };

    const [checked, setChecked] = useState(false);
    const handleChecked = () => {
        setChecked(!checked);
    };

    useEffect(() => {
        setMakeOwner(checked);
    }, [checked]);

    useEffect(() => {
        if (keycloak.hasResourceRole('admin', 'realm')) {
            setAdminCheckbox(true);
        }
    }, []);

    const renderError = () => <Center>{errors.map((error) => getProfileErrorText(error))}</Center>;

    return (
        <Box width="50%" margin={'0 auto'}>
            <Box marginTop="4em">
                <Heading>Create Game</Heading>
                <LicenseForm onSubmit={createGameLicense} />
                {adminCheckbox && (
                    <label className={'admin-checkbox-label'}>
                        <input type="checkbox" onChange={handleChecked} />
                        Assign license ownership to me
                    </label>
                )}
            </Box>
            {submittingGame && errors.length === 0 && (
                <Box display="flex" justifyContent="center">
                    <Spinner data-cy="loading" marginTop="1em" />
                </Box>
            )}

            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{errors.map((error) => getProfileErrorHeader(error))}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>{renderError()}</ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={onClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};
